import { Button, ButtonGroup, Checkbox } from '@timelyapp/react';
import Tooltip from 'components/Tooltip';
import { DateTime } from 'luxon';
import React, { memo, useMemo } from 'react';
import styles from './DatePickerFooter.module.scss';
import type { DatePickerMode } from './types';

interface Props {
  mode: DatePickerMode;
  enabledModes?: Array<DatePickerMode>;
  onChangeMode: (mode: DatePickerMode) => void;
  onChangeCustomRangeMode: () => void;
  customDateRangeEnabled: boolean;
  canToggleRangeMode: boolean;
  onReturnToBaseDate: (e: React.MouseEvent<HTMLElement>) => void;
}

const DatePickerFooter = (props: Props) => {
  const {
    onChangeMode,
    mode,
    enabledModes,
    onChangeCustomRangeMode,
    customDateRangeEnabled,
    canToggleRangeMode,
    onReturnToBaseDate,
  } = props;

  const options = useMemo(
    () =>
      [
        {
          value: 'day' as const,
          label: 'Day',
        },
        {
          value: 'week' as const,
          label: 'Week',
        },
        {
          value: 'month' as const,
          label: 'Month',
        },
        {
          value: 'year' as const,
          label: 'Year',
        },
      ].filter(option => enabledModes?.includes(option.value)),
    [enabledModes]
  );

  return (
    <div className={styles.container}>
      <div className={styles.row}>
        {canToggleRangeMode && (
          <Tooltip position="right" title="Custom start and end date">
            <Checkbox
              label="Date range"
              checked={customDateRangeEnabled}
              onChange={onChangeCustomRangeMode}
            />
          </Tooltip>
        )}
        <div
          className={styles.backToBase}
          style={{ visibility: mode === 'year' ? 'hidden' : 'visible' }}
        >
          <Button onClick={onReturnToBaseDate} priority="low" size="small">
            <Tooltip title="Select today">
              {mode !== 'day' ? `This ${mode}` : `Today, ${DateTime.now().toFormat('MMM dd')}`}
            </Tooltip>
          </Button>
        </div>
      </div>
      {enabledModes && (
        <ButtonGroup
          stretch
          style={{ marginTop: 8 }}
          options={options}
          activeValue={mode}
          onChange={onChangeMode}
        />
      )}
    </div>
  );
};

export default memo(DatePickerFooter);
